import { CONFIG } from '../config/constants.js';

/**
 * Calculates trade output based on dice rolls and exchange rate
 * @param {string} fromResource - Resource to trade away
 * @param {string} toResource - Resource to receive
 * @param {number} diceTotal - Total from dice rolls
 * @returns {Object} { cost, gained, valid, fromResource, toResource, rate }
 */
export function calculateTrade(fromResource, toResource, diceTotal) {
  const rate = CONFIG.TRADE_EXCHANGE_RATES[fromResource]?.[toResource];

  if (!rate) {
    return { cost: 0, gained: 0, valid: false };
  }

  // Dice total determines volume of trade
  // Each point of dice = 1 unit of fromResource traded
  const unitsToTrade = diceTotal;
  const unitsReceived = Math.floor(unitsToTrade * rate);

  if (unitsReceived === 0) {
    return { cost: 0, gained: 0, valid: false, reason: 'notWorthTrading', fromResource, toResource, rate };
  }

  return {
    cost: unitsToTrade,
    gained: unitsReceived,
    valid: true,
    fromResource,
    toResource,
    rate
  };
}

/**
 * Validates if a trade can be executed
 * @param {Object} tradeResult - Result from calculateTrade
 * @param {Object} currentResources - Current resource amounts
 * @returns {boolean} True if trade is valid
 */
export function canExecuteTrade(tradeResult, currentResources) {
  if (!tradeResult.valid) return false;

  const currentAmount = currentResources[tradeResult.fromResource] || 0;
  return currentAmount >= tradeResult.cost;
}

/**
 * Executes a trade and returns updated resources
 * @param {Object} tradeResult - Result from calculateTrade
 * @param {Object} currentResources - Current resource amounts
 * @returns {Object} Updated resources
 */
export function executeTrade(tradeResult, currentResources) {
  return {
    ...currentResources,
    [tradeResult.fromResource]: currentResources[tradeResult.fromResource] - tradeResult.cost,
    [tradeResult.toResource]: (currentResources[tradeResult.toResource] || 0) + tradeResult.gained
  };
}

/**
 * Get available trade resources (resources that can be traded)
 * @param {Object} currentResources - Current resource amounts
 * @returns {Array} Array of tradeable resource names
 */
export function getTradeableResources(currentResources) {
  const tradeable = ['food', 'wood', 'stone', 'metal'];
  return tradeable.filter(r => (currentResources[r] || 0) > 0);
}
